import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Card,
  Typography,
  Radio,
  Button,
  Upload,
  AutoComplete
} from 'antd';
import { adminSentenceApi } from '@/services/api';
import { 
  PlusOutlined,
  MinusCircleOutlined,
  PictureOutlined,
  CloseOutlined
} from '@ant-design/icons';

const { Title } = Typography;

interface QuestionItem {
  id: string;
  sentence: string;
  sentenceId?: number;
  answer: string;
  imageUrl?: string;
}

interface StoryDialogQuestionSectionProps {
  onQuestionChange?: (questions: QuestionItem[]) => void;
  recordingEnabled?: boolean;
  onRecordingEnabledChange?: (enabled: boolean) => void;
}

const StoryDialogQuestionSection: React.FC<StoryDialogQuestionSectionProps> = ({
  onQuestionChange,
  recordingEnabled = false,
  onRecordingEnabledChange
}) => {
  const form = Form.useFormInstance();
  const [questions, setQuestions] = useState<QuestionItem[]>([
    { id: '1', sentence: '', answer: '' }
  ]);
  const [options, setOptions] = useState<{ key: string; value: string; label: string; item: any }[]>([]);
  const timerRef = React.useRef<number | undefined>(undefined);

  // 폼 배열 변화 감지 (상위에서 복원 시 동기화)
  const watchedSentences = Form.useWatch('sdq_sentences', form) as string[] | undefined;
  const watchedSentenceIds = Form.useWatch('sdq_sentenceIds', form) as (number | undefined)[] | undefined;
  const watchedAnswers = Form.useWatch('sdq_answers', form) as string[] | undefined;
  const watchedImageUrls = Form.useWatch('sdq_imageUrls', form) as string[] | undefined;
  // 초기 로드: 폼의 hidden 배열 필드에서 질문 리스트 복원
  React.useEffect(() => {
    try {
      const sentences = form.getFieldValue('sdq_sentences') as string[] | undefined;
      const sentenceIds = form.getFieldValue('sdq_sentenceIds') as (number | undefined)[] | undefined;
      const answers = form.getFieldValue('sdq_answers') as string[] | undefined;
      const imgs = form.getFieldValue('sdq_imageUrls') as string[] | undefined;
      if (Array.isArray(sentences) && sentences.length > 0) {
        const restored: QuestionItem[] = sentences.map((s, idx) => ({
          id: String(idx + 1),
          sentence: s || '',
          sentenceId: sentenceIds && sentenceIds[idx] != null ? Number(sentenceIds[idx]) : undefined,
          answer: (answers && answers[idx]) || '',
          imageUrl: (imgs && imgs[idx]) || undefined,
        }));
        setQuestions(restored);
        // Form 개별 필드도 채워서 UI와 검증 동기화
        const kv: Record<string, any> = {};
        restored.forEach(q => {
          kv[`sentence_${q.id}`] = q.sentence;
          kv[`answer_${q.id}`] = q.answer;
        });
        form.setFieldsValue(kv);
      }
    } catch (_) {}
  }, [form]);

  // 상위 폼 배열 값 변경 시 내부 상태/개별 필드 동기화
  React.useEffect(() => {
    if (Array.isArray(watchedSentences) && watchedSentences.length > 0) {
      const restored: QuestionItem[] = watchedSentences.map((s, idx) => ({
        id: String(idx + 1),
        sentence: s || '',
        sentenceId: watchedSentenceIds && watchedSentenceIds[idx] != null ? Number(watchedSentenceIds[idx]) : undefined,
        answer: (watchedAnswers && watchedAnswers[idx]) || '',
        imageUrl: (watchedImageUrls && watchedImageUrls[idx]) || undefined,
      }));
      setQuestions(restored);
      const kv: Record<string, any> = {};
      restored.forEach(q => {
        kv[`sentence_${q.id}`] = q.sentence;
        kv[`answer_${q.id}`] = q.answer;
      });
      form.setFieldsValue(kv);
    }
  }, [watchedSentences, watchedSentenceIds, watchedAnswers, watchedImageUrls, form]);


  const toKo = {
    gender: (v: string) => (v === 'male' ? '남성' : v === 'female' ? '여성' : v || ''),
    age: (v: string) => (v === 'child' ? '아동' : v === 'young' ? '청년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : v || ''),
    speed: (v: string) => (v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : v || ''),
    pitch: (v: string) => (v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : v || ''),
  } as const;

  const searchSentences = (query: string) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      try {
        const res = await adminSentenceApi.list({ q: query, page: 0, size: 200 });
        const data = res?.data || res;
        const content = data?.content || data?.data?.content || [];
        const opts = content.map((s: any) => ({
          key: String(s.id),
          value: String(s.text || ''),
          label: `${s.text} (${toKo.gender(String(s.gender||''))}|${toKo.age(String(s.age||''))}|${toKo.speed(String(s.speed||''))}|${toKo.pitch(String(s.pitch||''))})`,
          item: s,
        }));
        setOptions(opts);
      } catch (_) {
        setOptions([]);
      }
    }, 250);
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `question_${Date.now()}`,
      sentence: '',
      answer: ''
    };
    const newQuestions = [...questions, newQuestion];
    setQuestions(newQuestions);
    onQuestionChange?.(newQuestions);
    syncFormArrays(newQuestions);
  };

  const removeQuestion = (questionId: string) => {
    if (questions.length > 1) {
      const newQuestions = questions.filter(q => q.id !== questionId);
      setQuestions(newQuestions);
      onQuestionChange?.(newQuestions);
      syncFormArrays(newQuestions);
    }
  };

  const syncFormArrays = (list: QuestionItem[]) => {
    const sentences = list.map(q => q.sentence || '');
    const sentenceIds = list.map(q => (q.sentenceId != null ? Number(q.sentenceId) : undefined));
    const answers = list.map(q => q.answer || '');
    form.setFieldValue('sdq_sentences', sentences);
    form.setFieldValue('sdq_sentenceIds', sentenceIds);
    form.setFieldValue('sdq_answers', answers);
    const imgs = list.map(q => q.imageUrl || '');
    form.setFieldValue('sdq_imageUrls', imgs);
  };

  const updateQuestion = (questionId: string, field: keyof QuestionItem, value: any) => {
    const newQuestions = questions.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    );
    setQuestions(newQuestions);
    onQuestionChange?.(newQuestions);
    syncFormArrays(newQuestions);
  };

  const handleImageUpload = (questionId: string, info: any) => {
    const candidate = (info?.file && (info.file as any).originFileObj) || info?.file;
    if (candidate) {
      const file = candidate as File;
      const previewUrl = URL.createObjectURL(file);
      updateQuestion(questionId, 'imageUrl', previewUrl);
      const idx = questions.findIndex(q => q.id === questionId);
      if (idx >= 0) {
        form.setFieldValue(`sdq_imageFile_${idx}`, file);
      }
    }
  };

  const removeImage = (questionId: string) => {
    updateQuestion(questionId, 'imageUrl', '');
  };

  return (
    <Card className='pb-5 border-b border-gray-200'>
      <Title level={4} className='mb-4'>스토리&다이얼로그_퀘스천</Title>
      
      <Form.Item
        label="녹음 여부"
        name="sdq_recordingEnabled"
        rules={[
          { required: true, message: '녹음 여부를 선택해주세요' }
        ]}
      >
        <Radio.Group onChange={(e) => onRecordingEnabledChange?.(e.target.value)}>
          <Radio value={true}>녹음</Radio>
          <Radio value={false}>미녹음</Radio>
        </Radio.Group>
      </Form.Item>

      <div className='space-y-6'>
        {questions.map((question, index) => (
          <div key={question.id} className='p-4 rounded-lg border border-gray-200'>
            <div className='flex justify-between items-center mb-4'>
              <h5 className='text-lg font-semibold'>문항 {index + 1}</h5>
              {questions.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => removeQuestion(question.id)}
                >
                  삭제
                </Button>
              )}
            </div>

            <div className='flex gap-4'>
              {/* 이미지 업로드 */}
              <div>
                <label className='block mb-2 text-sm font-medium'>이미지 업로드</label>
                <div className='w-[150px] h-[150px]'>
                  {question.imageUrl ? (
                    <div className='relative' style={{ width: '150px', height: '150px' }}>
                      <img 
                        src={question.imageUrl} 
                        alt="문항 이미지" 
                        className='object-cover rounded-lg border border-gray-200'
                        style={{ width: '150px', height: '150px' }}
                      />
                      <Button
                        type="primary"
                        shape="circle"
                        size="small"
                        icon={<CloseOutlined />}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          backgroundColor: '#ef4444',
                          borderColor: '#ef4444',
                          zIndex: 10
                        }}
                        onClick={() => removeImage(question.id)}
                      />
                    </div>
                  ) : (
                    <Upload
                      name="questionImage"
                      listType="picture-card"
                      className="question-image-uploader"
                      showUploadList={false}
                      action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
                      onChange={(info) => handleImageUpload(question.id, info)}
                      style={{ width: '150px', height: '150px' }}
                    >
                      <div className='flex flex-col justify-center items-center rounded-lg' style={{ width: '150px', height: '150px' }}>
                        <PictureOutlined className='text-2xl text-gray-400' />
                        <div className='mt-2 text-gray-400'>이미지 업로드</div>
                      </div>
                    </Upload>
                  )}
                </div>
              </div>

              {/* 문장 및 정답 입력 */}
              <div className='space-y-4 w-full'>
                <Form.Item label="문장 선택" name={`sentence_${question.id}`} rules={[{ required: true, message: '문장을 선택해주세요' }]}>
                  <AutoComplete
                    placeholder="문장을 검색/선택하세요"
                    size="large"
                    value={question.sentence}
                    onFocus={() => searchSentences('')}
                    onSearch={searchSentences}
                    options={options}
                    filterOption={false}
                    dropdownMatchSelectWidth={800}
                    style={{ width: '100%', height: 48, fontSize: 16 }}
                    onSelect={(_val, option: any) => {
                      const text = option?.item?.text ?? _val;
                      const sid = option?.item?.id != null ? Number(option.item.id) : undefined;
                      // question state에 sentence와 sentenceId 동시 반영 후 배열 동기화
                      const updated = questions.map((q, i) => (
                        q.id === question.id ? { ...q, sentence: text, sentenceId: sid } : q
                      ));
                      setQuestions(updated);
                      onQuestionChange?.(updated);
                      syncFormArrays(updated);
                    }}
                    onChange={(val) => updateQuestion(question.id, 'sentence', val)}
                  />
                </Form.Item>

                <Form.Item
                  label="정답 입력"
                  name={`answer_${question.id}`}
                  rules={[
                    { required: true, message: '정답을 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="정답을 입력하세요"
                    size="large"
                    value={question.answer}
                    onChange={(e) => updateQuestion(question.id, 'answer', e.target.value)}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-4'>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={addQuestion}
          size='large'
          block
        >
          문항 추가
        </Button>
      </div>

      {/* hidden array fields for payload build */}
      <Form.Item
        name='sdq_sentences'
        hidden
        rules={[
          {
            validator: (_rule, value) => {
              const list = Array.isArray(value) ? value.filter((s) => !!String(s).trim()) : [];
              return list.length > 0
                ? Promise.resolve()
                : Promise.reject(new Error('스토리&다이얼로그_퀘스천 문항을 한 개 이상 등록해주세요'));
            }
          }
        ]}
      >
        <input />
      </Form.Item>
      <Form.Item name='sdq_sentenceIds' hidden>
        <input />
      </Form.Item>
      <Form.Item name='sdq_answers' hidden>
        <input />
      </Form.Item>
      <Form.Item name='sdq_imageUrls' hidden>
        <input />
      </Form.Item>
    </Card>
  );
};

export default StoryDialogQuestionSection; 